/**
 * <head> markup for the statically generated pages.
 *
 * The generator (Workstream B) replaces the CRA template's default title,
 * description, canonical, robots and OG/Twitter tags with this output. The
 * client uses pageTitle() so document.title matches after navigation.
 */
import { describePeriod, formatGap, formatInt, formatMoneyCompact, gapDirection, isNum } from './format';
import { DEFAULT_SITE_ORIGIN, SITE_NAME, ogImagePath, pagePath } from './siteConfig';

const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** JSON for inline <script> blocks: never allow "</script" or "<!--" through. */
export const safeJson = (value) =>
  JSON.stringify(value).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026');

function clip(text, max = 160) {
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  return `${cut.slice(0, cut.lastIndexOf(' '))}\u2026`;
}

function gapTitleFragment(gap) {
  const direction = gapDirection(gap);
  if (direction === 'above') return `sold ${formatGap(gap).replace('+', '')} above guide`;
  if (direction === 'below') return `sold ${formatGap(gap).replace('\u2212', '')} below guide`;
  if (direction === 'at') return 'selling at guide';
  return null;
}

export function pageTitle(type, payload) {
  if (type === 'suburb' && payload) {
    const fragment = gapTitleFragment(payload.headline?.guide_gap_pct);
    return fragment
      ? `${payload.name} property: ${fragment} | ${SITE_NAME}`
      : `${payload.name} property prices and auction results | ${SITE_NAME}`;
  }
  if (type === 'agent' && payload) {
    const agency = payload.agency?.name ? `, ${payload.agency.name}` : '';
    return `${payload.name}${agency}: price guide record | ${SITE_NAME}`;
  }
  if (type === 'suburb-index') return `Sydney suburbs by price guide gap | ${SITE_NAME}`;
  if (type === 'methodology') return `How we measure price guides | ${SITE_NAME}`;
  return `Page not found | ${SITE_NAME}`;
}

export function pageDescription(type, payload) {
  if (type === 'suburb' && payload) {
    const h = payload.headline || {};
    const where = `${payload.name}${payload.postcode ? ` ${payload.postcode}` : ''}`;
    const parts = [];
    if (isNum(h.guide_gap_pct)) {
      const direction = gapDirection(h.guide_gap_pct);
      const words =
        direction === 'at'
          ? 'sold at about their price guide'
          : `sold a median ${formatGap(h.guide_gap_pct).replace(/^[+\u2212]/, '')} ${direction} guide`;
      parts.push(`${where}: ${formatInt(h.guide_matched_sales)} homes with a published guide in ${describePeriod(payload.data_period)} ${words}.`);
    } else {
      parts.push(`${where} property sales, price guides and results.`);
    }
    if (isNum(h.median_sale_price)) parts.push(`Median price ${formatMoneyCompact(h.median_sale_price)}.`);
    parts.push('Recent results against guide and local agents.');
    return clip(parts.join(' '));
  }
  if (type === 'agent' && payload) {
    const h = payload.headline || {};
    const agency = payload.agency?.name ? ` (${payload.agency.name})` : '';
    const lead = isNum(h.guide_gap_pct)
      ? `${payload.name}${agency}: ${formatInt(h.guide_matched_sales)} sales with a published guide, results on average ${formatGap(h.guide_gap_pct)} against guide.`
      : `${payload.name}${agency}: sales and price guide record.`;
    return clip(`${lead} Recent results, suburbs and how the figures are measured.`);
  }
  if (type === 'suburb-index') {
    return 'Every Sydney suburb we track, with how far sale results land from advertised price guides, median prices and sample sizes.';
  }
  if (type === 'methodology') {
    return 'Where RealEstats data comes from, how advertised price guides are matched to sale results, and what the guide gap and typical miss mean.';
  }
  return 'This page does not exist. Browse Sydney suburbs by price guide gap instead.';
}

function breadcrumbItems(type, payload) {
  const items = [{ name: 'Sydney', path: '/suburbs' }];
  if (type === 'suburb' && payload) {
    items.push({ name: payload.name, path: pagePath('suburb', payload.slug) });
  } else if (type === 'agent' && payload) {
    const main = payload.suburbs?.find((s) => s.has_page);
    if (main) items.push({ name: main.name, path: pagePath('suburb', main.slug) });
    items.push({ name: payload.name, path: pagePath('agent', payload.slug) });
  } else if (type === 'methodology') {
    items.push({ name: 'Methodology', path: '/methodology' });
  }
  return items;
}

export function structuredData(type, payload, origin) {
  const url = (path) => `${origin}${path}`;
  const blocks = [];
  const crumbs = breadcrumbItems(type, payload);
  if (crumbs.length > 1) {
    blocks.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: crumbs.map((c, i) => ({ '@type': 'ListItem', position: i + 1, name: c.name, item: url(c.path) })),
    });
  }
  if (type === 'suburb' && payload) {
    blocks.push({
      '@context': 'https://schema.org',
      '@type': 'Place',
      name: `${payload.name}, NSW`,
      url: url(pagePath('suburb', payload.slug)),
      address: {
        '@type': 'PostalAddress',
        addressLocality: payload.name,
        addressRegion: payload.state || 'NSW',
        ...(payload.postcode ? { postalCode: payload.postcode } : {}),
        addressCountry: 'AU',
      },
      ...(payload.lga ? { containedInPlace: { '@type': 'AdministrativeArea', name: payload.lga.name } } : {}),
    });
  }
  if (type === 'agent' && payload) {
    blocks.push({
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: payload.name,
      url: url(pagePath('agent', payload.slug)),
      jobTitle: 'Real estate agent',
      ...(payload.agency?.name ? { worksFor: { '@type': 'RealEstateAgent', name: payload.agency.name } } : {}),
    });
  }
  if (type === 'methodology') {
    blocks.push({
      '@context': 'https://schema.org',
      '@type': 'Dataset',
      name: 'Sydney residential price guide accuracy',
      description: pageDescription('methodology', null),
      url: url('/methodology'),
      creator: { '@type': 'Organization', name: SITE_NAME, url: origin },
      spatialCoverage: { '@type': 'Place', name: 'Greater Sydney, NSW, Australia' },
      isAccessibleForFree: true,
    });
  }
  return blocks;
}

export function canonicalPath(type, payload) {
  if (type === 'suburb' || type === 'agent') return payload ? pagePath(type, payload.slug) : null;
  if (type === 'suburb-index') return '/suburbs';
  if (type === 'methodology') return '/methodology';
  return null;
}

export function isIndexable(type, payload) {
  if (type === 'not-found') return false;
  if (type === 'suburb' || type === 'agent') return payload?.indexable === true;
  return true;
}

export function buildHead(type, payload, siteOrigin = DEFAULT_SITE_ORIGIN) {
  const origin = siteOrigin.replace(/\/+$/, '');
  const title = pageTitle(type, payload);
  const description = pageDescription(type, payload);
  const canonical = canonicalPath(type, payload);
  const image =
    (type === 'suburb' || type === 'agent') && payload
      ? `${origin}${ogImagePath(type, payload.slug)}`
      : `${origin}/og.png`;
  const robots = isIndexable(type, payload) ? 'index,follow,max-image-preview:large' : 'noindex,follow';

  const tags = [
    `<title>${escapeHtml(title)}</title>`,
    `<meta name="description" content="${escapeHtml(description)}" />`,
    `<meta name="robots" content="${robots}" />`,
  ];
  if (canonical) tags.push(`<link rel="canonical" href="${escapeHtml(origin + canonical)}" />`);
  tags.push(
    `<meta property="og:site_name" content="${SITE_NAME}" />`,
    `<meta property="og:type" content="${type === 'agent' ? 'profile' : 'website'}" />`,
    `<meta property="og:title" content="${escapeHtml(title.replace(` | ${SITE_NAME}`, ''))}" />`,
    `<meta property="og:description" content="${escapeHtml(description)}" />`,
    ...(canonical ? [`<meta property="og:url" content="${escapeHtml(origin + canonical)}" />`] : []),
    `<meta property="og:image" content="${escapeHtml(image)}" />`,
    '<meta property="og:image:width" content="1200" />',
    '<meta property="og:image:height" content="630" />',
    `<meta property="og:locale" content="en_AU" />`,
    '<meta name="twitter:card" content="summary_large_image" />',
    `<meta name="twitter:title" content="${escapeHtml(title.replace(` | ${SITE_NAME}`, ''))}" />`,
    `<meta name="twitter:description" content="${escapeHtml(description)}" />`,
    `<meta name="twitter:image" content="${escapeHtml(image)}" />`,
  );
  for (const block of structuredData(type, payload, origin)) {
    tags.push(`<script type="application/ld+json">${safeJson(block)}</script>`);
  }
  return tags.join('\n    ');
}

export { breadcrumbItems };
